<template>

  <div id="helpPopupBox" class="helpPopup">
    <div class="help-popup-content">
      <div class="help-popup-header">
        <span class="help_close" v-on:click="closeHelpModal">×</span>
        <h2>Help</h2>
      </div>
      <div class="help-popup-main">
      <h3>
        About this tool
      </h3>
      <p>
        SPURS (Strategic Planning Uncertainty Reduction Solution) is a Monte Carlo
        simulation tool developed by EY-Parthenon.
      </p>
      <p>
        SPURS allows the user to capture and consider uncertainty at any stage of a model, such as a business plan.
        SPURS follows the paradigm of "visual programming" where users can create complex networks of data flows by creating nodes and linking them.
      </p>
      <h4>
        Basic commands and advice
      </h4>
      <p>
        <ul>
          <li>The context menu and some other functions are available on a mouse/trackpad <em>right-click</em>.</li>
          <li><b>Undo / redo:</b> Ctrl + Z and Ctrl + Y (on Windows).
          Do <em>not</em> use the browser back/forward buttons as you may then accidentally leave the app.</li>
          <li><b>Panning:</b> left-click on empty space and drag the the pressed left mouse button.</li>
          <li><b>Zooming</b> in and out of the editor: mousewheel</li>
          <li><b>Selecting multiple nodes at once</b>: keep Ctrl pressed and left-click nodes with the mouse.</li>
          <li><b>Copying and pasting one or more selected nodes</b>: Ctrl + C and Strg + V.</li>
          <li><b>Deleting nodes:</b> select them and then press Delete on the keyboard.</li>
        </ul>
      </p>
      <p>
        SPURS works with distributions. You <em>cannot</em> use it to add two single numbers and visualise the result. But you can add a number to a distribution and visualise the result.
      </p>
      <p>
        SPURS has been tested to work with the browsers Chrome and Firefox. Screenshots below show the app being used in Chrome.
      </p>
      <h3>
        Quick links
      </h3>
      <ul>
        <li><a href="#advantages">Advantages of Monte Carlo simulations</a></li>
        <li><a href="#overview_functionality">Overview basic functionality</a></li>
        <li><a href="#entering">Entering input parameters and choosing distributions</a></li>
        <ul>
          <li><a href="#adding_a_distribution">Adding a distribution</a></li>
          <li><a href="#available_distributions">Overview of available distributions</a></li>
        </ul>
        <li><a href="#calculating">Calculating the desired KPIs</a></li>
        <ul>
          <li><a href="#linking_nodes">Linking nodes and changing names of input parameter</a></li>
          <li><a href="#add_and_overlay">Using "Add" and "Overlay"</a></li>
        </ul>
        <li><a href="#visualisation">Improving the visualisation using grouping and modules</a></li>
          <ul>
            <li><a href="#commenting">Commenting</a></li>
            <li><a href="#grouping">Grouping</a></li>
            <li><a href="#modules">Modules</a></li>
          </ul>
        <li><a href="#assessing">Assessing the impact of input parameters</a></li>
        <li><a href="#popup">Node details pop-up</a></li>
      </ul>
      <h3 id="advantages">
        Advantages of Monte Carlo simulations
      </h3>
      <p>
        The figure below shows the advantages of Monte Carlo simulations compared to simple case-based modelling / scenario analysis.
      </p>
      <p>
        In case-based modelling, only a limited number of cases can be considered.
        Common practice are three scenarios: a best case, a base case, and a worst case.
        For each case, all variables are typically pre-defined and typically set in unison. E.g. in the best case all variables with a positive impact are set to high, and all variables with a negative impact are set to low. A Monte Carlo simulation, however, considers the uncertainty of each input parameter. Values are sampled from an underlying distribution and an infinite number of cases can be modelled at once.
      </p>
      <p>
        Using Monte Carlo simulations, it is possible to derive what impact a specific input parameter has on the result.
        Thus, deriving actionable insights is much easier using Monte Carlo simulations.
      </p>
      <p>
        Furthermore, a Monte Carlo simulation makes it easier to understand the range and probability of specific outcomes is easier, such as the expected profit of an operation.
      </p>
      <img alt="Advantages of Monte Carlo simulations" src="../../assets/help/advantages_monte_carlo_simulation.png" width="800px" >
      <h3 id="overview_functionality">
        Overview basic functionality
      </h3>
      <p>
        SPURS allows the user to:
        <ul>
          <li>create nodes that represent, for example, the input parameters to a model or operations</li>
          <li>intuitively link nodes to create data flows</li>
          <li>highlight ranges in a distribution to understand how predecessor nodes can lead to this result</li>
          <li>export models and load previously generated ones</li>
          <li>create re-usable modules</li>
        </ul>  
      </p>
      <p>
        The following figure shows the core functionality of SPURS.
        In short, the SPURS tool allows users to create a driver tree via intuitive "drag and drop".
        These driver trees can then be discussed and experimented with in an interactive way.
      </p>
      <img alt="SPURS core functionality" src="../../assets/help/core_functionality.png" width="800px" >
      <h3 id="entering">
        Entering input parameters and choosing distributions
      </h3>
      <p>
        SPURS often uses mouse <em>right-clicks</em> to add and change elements.
      </p>
      <h4 id="adding_a_distribution">
        Adding a distribution
      </h4>
      <p>
        To add a distribution node, right-click on a free area in the editor.
        Hover over the menu where it says "Distribution", then left-click on the desired distribution.
        In order to start the simulation, distributions require one or more parameters to be set.
      </p>
      <img alt="Entering input parameters and choosing distributions" src="../../assets/help/a1.png" width="800px" >
      <h4 id="available_distributions">
        Overview of available distributions
      </h4>
      <p>
        The currently available distributions are shown in the figure below.
        The distribution should reflect the expected distribution of the input parameters.
        Historical data can be used to identify which distribution is suitable, but history is not always a good predictor of the future.
        Expert knowledge may be required to include information about new developments.
      </p>
      <img alt="Entering input parameters and choosing distributions" src="../../assets/help/a2.png" width="800px" >
      
      <h3 id="calculating">
        Calculating the desired KPIs
      </h3>

      <h4 id="linking_nodes">
        Operation nodes, linking nodes and changing names of input parameter
      </h4>

      <p>
        Nodes for operations take inputs, perform some mathematical operation and return an output.
        This way, for instance, a business plan can be modelled by deducting a cost distribution from a revenue distribution.
        Once linked together, the data flows from node to node and gets processed.
        The input parameters can be renamed by right-clicking on them.
      </p>
      
      <img alt="Linking nodes and changing names of input parameter" src="../../assets/help/b1.png" width="800px" >
      
      <h4 id="add_and_overlay">
        Using "Add" and "Overlay"
      </h4>
      
      <p>
        The operations "Add" and "Overlay" can be easily confused.
        "Add" performs a simple addition. Values of one distribution are added to values of another distribution.
        "Overlay" is different: It allows to combine to equally similar scenarios in one distribution.
        This corresponds to a logical OR.
        Overlay can also be used to fuse two distributions to create a new one that is not available in the standard set.
      </p>
      
      <img alt="Entering input parameters and choosing distributions" src="../../assets/help/b2.png" width="800px" >

      <h3 id="visualisation">
        Improving the visualisation using grouping and modules
      </h3>
      <h4 id="commenting">
        Commenting
      </h4>
      <p>
        It is possible to add comments.
        These comments can either be placed anywhere in the editor or they can be placed in a way that they touch a node.
        If comments touch a node, they become "sticky" and will move together with the node when the node is moved.
      </p>
      <img alt="Commenting" src="../../assets/help/commenting.png" width="400px" >
      <h4 id="grouping">
        Grouping
      </h4>
      <p>
        <ul>
          <li>
            In order to group elements, keep the Ctrl key pressed and left-clicking on the nodes that shall be grouped.
            The yellow border shows which nodes have been selected.
        </li>
        <li>Now, right-click on an empty area in the editor and left-click "Grouping" to create the group.</li>
        </ul>        
      </p>
      <img alt="Creating a group" src="../../assets/help/grouping1.png" width="400px" >
      <ul>
        <li>A small menu will appear where you can choose a color for the group and select a name.</li>
      </ul>
      <img alt="Choosing a color and name for a group" src="../../assets/help/grouping2.png" width="400px" >
      <p>     
        <ul>
          <li>Elements within a group will move together with the group. You can rearrange elements within a group by dragging them within the group.</li>
          <li>To change the name or the color of a group again, right-click on empty space within the group's boundaries.</li>
          <li>To delete a group, left-click the group and hit delete on the keyboard.</li>
        </ul>
      </p>

      <h4 id="modules">
        Modules
      </h4>
      <p>
        Modules allow to define models that can be re-used and imported as a single node.
        This way, the complexity of a model part can be abstracted away and hidden behind a single node.
      </p>
      <p>
        To create a module, click on the (+) button on the right-hand side of the editor.
        This will create a new module.
      </p>
      <img alt="Creating a module" src="../../assets/help/create_module.png" width="250px" >
      <p>
        The name (initially "spurs1.module") can be changed by <em>right-clicking</em> on it.
        By left-clicking on the module names on the right side, you can change the currently active module.
      </p>
      <img alt="Renaming a module" src="../../assets/help/renaming_module.png" width="200px" >
      <p>
        A module needs one (or more) input nodes, and one (or more) output nodes.
        To create these, right-click on some empty space in the editor. Make sure to be in the right module.
        In the context menu, go to Module, and then select either input or output nodes.
        Other nodes, such as distribution nodes, can be added as usual.
      </p>
      <p> To delete a created module, just change the name of that module to an empty string.
      </p>
      <p>
        To re-use / import a module, right-click on an empty space in the editor. Then select Module > Module.
        This creates a module node.
      </p>
      <img alt="Editing module" src="../../assets/help/edit_module.png" width="300px" >
      <p>
        Please enter the name of the module that you want to import into the input field of the module node.
        Make sure to use the exact spelling as the name of the module you want to import.
      </p>
      <p>
        You <em>must</em> provide a name for each input and output node of the module.
        Otherwise, it will not be possible to connect other nodes to the imported module.
      </p>
      <img alt="Naming input and output nodes of a module" src="../../assets/help/name_input_output_nodes.png" width="400px" >
      <h3 id="assessing">
        Assessing the impact of input parameters
      </h3>

      <p>
        The impact of input parameters on a KPI can be assessed using this tool.
        After clicking on the distribution preview of the node that represents the relevant KPI, a range in the distribution can be selected.
        For example, the node may represent the expected profit. We are now interested which constellations of input parameters lead to a negative profit.
        After setting the range in a node and leaving the details popup, this node will now show a yellow indicator.
        The details popups of all other nodes will now automatically highlight those parts of their distributions that would lead to a result withing the selected range of the yellow-indicated node. 
      </p>
      
      <img alt="Entering input parameters and choosing distributions" src="../../assets/help/d.png" width="800px" >
      
      <h3 id="popup">
        Node details popup
      </h3>

      <h4>Distribution</h4>

      The probability density distribution shown in the popup is the KDE (Kernel Density Estimation) of the underlying data.
      Thus, it is 'smoother' than the distirbution of the actual underlying data points.

      <h4> Top drivers of uncertainty (nodes with largest distribution difference)</h4>
      The divergence is measured between original density function and the yellow density function resulting from selecting a range.
      The degree of deviation is measured using the Kullback–Leibler (KL) divergence.
      KL divergence is "measure of how one probability distribution is different from a second, reference probability distribution" (Wikipedia).

      The bar plot below shows up to 10 nodes with the largest deviation.
      Looking at upstream nodes, these can be understood as the main drivers of uncertainty.
      Constant values would, thus, not appear in this list. 
      <br>
      Currently, this includes nodes up- and downstream.

      <h4>What is a boxplot?</h4>
      The figure below shows a boxplot. A boxplot aims to show the key characteristics of a distribution.
      The so-called interquartile range (IQR) is the difference between the 75th and 25th percentiles (the "height of the box").
      By convention, the maximum length of a whisker is 1.5 times the IQR. All data points beyond this are considered outliers.
      <br>
      <br>
      <img src="../../assets/boxplot_illustration.svg" alt="Boxplot illustration" align="left" width="200px">

      <h4>Download data</h4>
      When importing into Excel, please make sure that the data format is correctly read, e.g. the decimal point is a point.
      Ideally, open the CSV file with a text editor first to see unaltered data.

      </div>
    </div>
  </div>

</template>


<script>

  import Vue from 'vue';

  export default {

    name: 'HelpPopUp',
    props: [],
    data: () => {
      return {
        
      }
    },
    mounted() {
     // console.log('I am mounted!')

     // This is an event listener for when the user clicks next to the popup.
     // This send sends a signal to not show the popup anymore.
     window.onclick = function(event) {
          let helpPopupBox = document.getElementById('helpPopupBox');
          if (event.target == helpPopupBox) {
            // console.log('Command send to close help.')
            Vue.prototype.$eventHub.$emit("showHelpPopUpEvent", false);
          }
        }
    },

    methods: {

      // The method below is called when the user clicks the 'x' in the popup.
      // See the v-on:click directive
      closeHelpModal(){
        Vue.prototype.$eventHub.$emit("showHelpPopUpEvent", false);
      }

    }
  }

</script>


<style scoped>

  img {
    display: block;
  }

/* Scrollbar */

/* width */
::-webkit-scrollbar {
  width: 10px;
}

/* Track */
::-webkit-scrollbar-track {
  background: #f1f1f1; 
}
 
/* Handle */
::-webkit-scrollbar-thumb {
  background: #888; 
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #555; 
}

/* Popup Box */
  
  #helpPopupBox {
    z-index: 100;
  }

  /* mPopup box style */
  .helpPopup {
    position: fixed;
    z-index: 1;
    padding-top: 50px;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background-color: rgb(0,0,0);
    background-color: rgba(0,0,0,0.4);
  }

  .help-popup-content {
    position: relative;
    background-color: #fefefe;
    margin: auto;
    padding: 0;
    width: 1100px;
    box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2),0 6px 20px 0 rgba(0,0,0,0.19);
    -webkit-animation-name: animatetop;
    -webkit-animation-duration: 0.4s;
    animation-name: animatetop;
    animation-duration: 0.4s
  }

  .help-popup-header {
    padding: 2px 16px;
    background-color: #51A7DD;
    color: white;
  }

  .help-popup-main {
    /* This is the part of the pop-up underneath the pop-up header */

    padding: 20px 16px;
    overflow:auto;

  }

  .popup-footer {
    padding: 2px 16px;
    background-color: #999999;
    color: #ffffff;
    font-family: monospace;
    font-size: 12px;
  }

    /* close button style */
  .help_close {
    color: white;
    float: right;
    font-size: 28px;
    font-weight: bold;
  }
  .help_close:hover, .help_close:focus {
    color: #000;
    text-decoration: none;
    cursor: pointer;
  }

  
</style>